import { type FormEvent, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiFetch, type ApiError } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { Divider, GoogleButton } from '../components/GoogleButton'
import { detectWebView } from '../auth/detectWebView'
import { languageFromPath, withLang } from '../i18n/i18n'
import { useDocumentMeta } from '../i18n/useDocumentMeta'

export default function Signup() {
  const { signup } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('auth')
  const lang = languageFromPath(location.pathname)
  useDocumentMeta({ title: t('seo.titleSignup'), noindex: true })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [referralCode, setReferralCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // 409 = the email already has an account. Signup can't prove the inbox, so
  // it never takes that account over; the way in is a link sent to the inbox.
  const [exists, setExists] = useState(false)
  const [sendingLink, setSendingLink] = useState(false)
  const [linkSentTo, setLinkSentTo] = useState<string | null>(null)
  const webview = useMemo(() => detectWebView(), [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setExists(false)
    setSubmitting(true)
    try {
      const result = await signup(email, password, name || undefined, lang, referralCode || undefined)
      const next = result.has_orders ? withLang('/account', lang) : `${withLang('/destinations', lang)}?welcome=1`
      navigate(next, { replace: true })
    } catch (err) {
      if ((err as ApiError).status === 409) setExists(true)
      else setError(err instanceof Error ? err.message : t('signup.errorFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  async function sendLink() {
    setError(null)
    setSendingLink(true)
    try {
      await apiFetch<void>('/api/auth/magic/request', {
        method: 'POST',
        body: JSON.stringify({ email, locale: lang }),
      })
      setLinkSentTo(email)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.errorMagicSendFailed'))
    } finally {
      setSendingLink(false)
    }
  }

  if (linkSentTo) {
    return (
      <div className="page-stub" style={{ maxWidth: 420 }}>
        <h1 className="h1" style={{ marginBottom: 16 }}>{t('login.checkTitle')}</h1>
        <p style={{ fontSize: 15, lineHeight: 1.5, margin: '0 0 12px', wordBreak: 'break-word' }}>
          {t('login.checkBody', { email: linkSentTo })}
        </p>
        <p className="muted" style={{ fontSize: 14, lineHeight: 1.55, margin: 0 }}>
          {t('login.checkExpiry')}
        </p>
      </div>
    )
  }

  return (
    <div className="page-stub" style={{ maxWidth: 420 }}>
      <h1 className="h1" style={{ marginBottom: 24 }}>{t('signup.title')}</h1>
      {webview ? (
        <div className="webview-warn">
          <div className="webview-warn__title">
            {t('signup.webviewTitle', { name: webview.name })}
          </div>
          <p className="webview-warn__body">
            {t('signup.webviewBodyPart1')}
            <Link to={withLang('/login', lang)} style={{ color: 'var(--ink)' }}>
              {t('signup.webviewBodyLogin')}
            </Link>
            {t('signup.webviewBodyPart2')}
          </p>
          <p className="webview-warn__hint">
            {t('signup.webviewHint', { escape: webview.escapeHint })}
          </p>
        </div>
      ) : (
        <>
          <GoogleButton label={t('signup.googleLabel')} />
          <Divider />
        </>
      )}
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label className="label">{t('signup.labelName')}</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
          />
        </div>
        <div>
          <label className="label">{t('signup.labelEmail')}</label>
          <input
            className="input"
            type="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              setExists(false)
            }}
            autoComplete="email"
          />
        </div>
        <div>
          <label className="label">{t('signup.labelPassword')}</label>
          <input
            className="input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
          />
        </div>
        <div>
          <label className="label">Referral code (optional)</label>
          <input
            className="input"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
            placeholder="e.g. MARIA-7K2"
            autoCapitalize="characters"
          />
        </div>
        {exists && (
          <div
            style={{
              padding: '12px 14px',
              background: 'var(--accent-soft)',
              color: 'var(--accent)',
              borderRadius: 10,
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            <p style={{ margin: '0 0 10px' }}>{t('signup.existsBody')}</p>
            <button
              type="button"
              className="btn primary sm"
              onClick={() => void sendLink()}
              disabled={sendingLink}
            >
              {sendingLink ? t('login.submittingMagic') : t('signup.existsSendLink')}
            </button>
          </div>
        )}
        {error && <div style={{ color: 'var(--pop)', fontSize: 14 }}>{error}</div>}
        <button type="submit" className="btn primary" disabled={submitting}>
          {submitting ? t('signup.submitting') : t('signup.submit')}
        </button>
      </form>
      <p className="muted" style={{ marginTop: 24, fontSize: 14 }}>
        {t('signup.haveOne')}{' '}
        <Link to={withLang('/login', lang)} style={{ color: 'var(--ink)' }}>
          {t('signup.logIn')}
        </Link>
      </p>
    </div>
  )
}
